import {uniq} from '@tplatform/common';
import {useEffect} from 'react';

import {isCalltechParentMessage} from '../lib/is-calltech-parent-message';
import {resolveCalltechParentOrigin} from '../lib/resolve-calltech-parent-origin';

export type CalltechCallInfo = {
    phone?: string;
    queue?: string;
    chainId?: string;
    commutationId?: string;
    originalDN?: string;
    platformId?: string;
    direction?: string;
    accessToken?: string;
    app?: {
        permissions: Record<string, unknown>;
    };
    mockPhone?: {
        phone?: string;
    };
};

export type CalltechCall = {
    isEnded?: boolean;
    isMuted?: boolean;
    isOutgoing?: boolean;
    onHold?: boolean;
    info: CalltechCallInfo;
};

type CalltechMessageData = {
    args?: Array<{
        call?: CalltechCall;
    }>;
};

type UseCalltechBridgeParams = {
    calltechHost: string | null;
    calltechOrigins?: string[];
    phone?: string;
    onCall: (call: CalltechCall) => void;
};

const GET_STATE_MESSAGE = {calltech: 'v1', method: 'getState', args: []};

export const useCalltechBridge = ({
    calltechHost,
    calltechOrigins = [],
    phone,
    onCall,
}: UseCalltechBridgeParams) => {
    useEffect(() => {
        if (!calltechHost) {
            return;
        }

        const allowedOrigins = uniq([calltechHost, ...calltechOrigins]);
        const isTopLevel = window.self === window.top;
        const calltechParentOrigin = isTopLevel
            ? calltechHost
            : resolveCalltechParentOrigin({
                allowedOrigins,
                ancestorOrigin: window.location.ancestorOrigins?.[0],
                referrer: document.referrer,
            });

        if (!calltechParentOrigin) {
            return;
        }

        const handleCalltechMessage = (event: MessageEvent<CalltechMessageData>) => {
            if (!isCalltechParentMessage(
                event,
                calltechParentOrigin,
                isTopLevel ? undefined : window.parent,
            )) {
                return;
            }

            const lastCall = event.data?.args?.slice(-1)[0]?.call;

            if (!lastCall?.info) {
                return;
            }

            onCall(lastCall);
        };

        window.addEventListener('message', handleCalltechMessage);

        if (isTopLevel) {
            window.dispatchEvent(new MessageEvent('message', {
                data: {
                    args: [
                        {
                            call: {
                                onHold: false,
                                info: {
                                    phone,
                                    mockPhone: {phone},
                                    app: {
                                        permissions: {},
                                    },
                                },
                            },
                        },
                    ],
                },
                origin: calltechParentOrigin,
            }));
        } else {
            window.parent.postMessage(GET_STATE_MESSAGE, calltechParentOrigin);
        }

        return () => {
            window.removeEventListener('message', handleCalltechMessage);
        };
    }, [calltechHost, calltechOrigins, onCall, phone]);
};
